<template>
  <div class="min-h-screen bg-gray-50 dark:bg-gray-950 pb-8">
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-gray-900 dark:text-gray-100">
      <div class="mb-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 class="text-3xl font-bold text-gray-900 dark:text-white">Members Management</h1>
          <p class="text-base text-gray-600 dark:text-gray-400 mt-2">Manage member profiles, memberships, and personal information</p>
        </div>
        <div class="flex items-center gap-2">
          <input v-model="search" type="text" class="form-input" placeholder="Search members..." />
          <button class="btn btn-primary" @click="openAdd">Add Member</button>
        </div>
      </div>

      <!-- Member Statistics -->
      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div class="card dark:bg-gray-900 hover:shadow-lg transition-all duration-200">
          <div class="card-body p-6">
            <div class="flex items-center justify-between">
              <div>
                <p class="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">Total Members</p>
                <p class="text-3xl font-bold text-gray-900 dark:text-gray-100">{{ rows.length }}</p>
              </div>
              <div class="w-14 h-14 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center">
                <svg class="w-7 h-7 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
            </div>
          </div>
        </div>
        
        <div class="card dark:bg-gray-900 hover:shadow-lg transition-all duration-200">
          <div class="card-body p-6">
            <div class="flex items-center justify-between">
              <div>
                <p class="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">Active Members</p>
                <p class="text-3xl font-bold text-green-600">{{ activeMembersCount }}</p>
              </div>
              <div class="w-14 h-14 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center">
                <svg class="w-7 h-7 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                </svg>
              </div>
            </div>
          </div>
        </div>
        
        <div class="card dark:bg-gray-900 hover:shadow-lg transition-all duration-200">
          <div class="card-body p-6">
            <div class="flex items-center justify-between">
              <div>
                <p class="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">Expiring Soon</p>
                <p class="text-3xl font-bold text-yellow-600">{{ expiringSoonCount }}</p>
              </div>
              <div class="w-14 h-14 bg-yellow-100 dark:bg-yellow-900/30 rounded-full flex items-center justify-center">
                <svg class="w-7 h-7 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            </div>
          </div>
        </div>
        
        <div class="card dark:bg-gray-900 hover:shadow-lg transition-all duration-200">
          <div class="card-body p-6">
            <div class="flex items-center justify-between">
              <div>
                <p class="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">New This Month</p>
                <p class="text-3xl font-bold text-purple-600">{{ newThisMonthCount }}</p>
              </div>
              <div class="w-14 h-14 bg-purple-100 dark:bg-purple-900/30 rounded-full flex items-center justify-center">
                <svg class="text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
                </svg>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="card dark:bg-gray-900">
        <div class="card-body">
          <div class="overflow-x-auto">
            <table class="table">
              <thead class="table-header">
                <tr>
                  <th class="table-header-cell">Member</th>
                  <th class="table-header-cell">Contact</th>
                  <th class="table-header-cell">Plan</th>
                  <th class="table-header-cell">Status</th>
                  <th class="table-header-cell">Next Payment</th>
                  <th class="table-header-cell">Actions</th>
                </tr>
              </thead>
              <tbody class="table-body">
                <tr class="table-row" v-for="m in filtered" :key="m.id">
                  <td class="table-cell">
                    <div class="flex items-center">
                      <div class="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center mr-3">
                        <span class="text-sm font-medium text-gray-600">{{ m.name.charAt(0) }}</span>
                      </div>
                      <div>
                        <div class="font-medium">{{ m.name }}</div>
                        <div class="text-sm text-gray-500">ID: {{ m.memberId }}</div>
                      </div>
                    </div>
                  </td>
                  <td class="table-cell">
                    <div>{{ m.phone }}</div>
                    <div class="text-sm text-gray-500">{{ m.email }}</div>
                  </td>
                  <td class="table-cell">
                    <div>{{ m.plan }}</div>
                    <div class="text-sm text-gray-500">ETB {{ m.price }}/month</div>
                  </td>
                  <td class="table-cell">
                    <span :class="m.active ? 'badge badge-success' : 'badge badge-danger'">
                      {{ m.active ? 'Active' : 'Inactive' }}
                    </span>
                  </td>
                  <td class="table-cell">
                    <div>{{ m.nextPayment }}</div>
                    <div class="text-sm text-gray-500">{{ getDaysUntilPayment(m.nextPayment) }}</div>
                  </td>
                  <td class="table-cell">
                    <div class="flex gap-2">
                      <button class="btn btn-outline btn-sm" @click="edit(m)">Edit</button>
                      <button class="btn btn-info btn-sm" @click="viewProfile(m)">Profile</button>
                      <button class="btn btn-danger btn-sm" @click="remove(m)">Delete</button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <!-- Add/Edit Member Modal -->
      <div v-if="open" class="modal-overlay" @click="close">
        <div class="modal-content max-h-[90vh] flex flex-col" @click.stop>
          <div class="modal-header flex-shrink-0">
            <h3 class="text-lg font-semibold">{{ form.id ? 'Edit Member' : 'Add Member' }}</h3>
          </div>
          <div class="modal-body flex-1 overflow-y-auto">
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label class="form-label">Full Name</label>
                <input v-model="form.name" type="text" class="form-input" placeholder="Jane Doe" />
              </div>
              <div>
                <label class="form-label">Member ID</label>
                <input v-model="form.memberId" type="text" class="form-input" placeholder="M001" />
              </div>
              <div>
                <label class="form-label">Phone</label>
                <input v-model="form.phone" type="tel" class="form-input" placeholder="+123456789" />
              </div>
              <div>
                <label class="form-label">Email</label>
                <input v-model="form.email" type="email" class="form-input" placeholder="jane@example.com" />
              </div>
              <div>
                <label class="form-label">Date of Birth</label>
                <input v-model="form.dateOfBirth" type="date" class="form-input" />
              </div>
              <div>
                <label class="form-label">Gender</label>
                <select v-model="form.gender" class="form-input">
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                  <option value="Other">Other</option>
                </select>
              </div>
              <div>
                <label class="form-label">Plan</label>
                <select v-model="form.plan" class="form-input">
                  <option value="Basic">Basic</option>
                  <option value="Premium">Premium</option>
                  <option value="VIP">VIP</option>
                  <option value="Student">Student</option>
                </select>
              </div>
              <div>
                <label class="form-label">Monthly Price (ETB)</label>
                <input v-model="form.price" type="number" class="form-input" placeholder="500" />
              </div>
              <div>
                <label class="form-label">Join Date</label>
                <input v-model="form.joinDate" type="date" class="form-input" />
              </div>
              <div>
                <label class="form-label">Next Payment</label>
                <input v-model="form.nextPayment" type="date" class="form-input" />
              </div>
              <div class="md:col-span-2">
                <label class="form-label">Emergency Contact</label>
                <input v-model="form.emergencyContact" type="text" class="form-input" placeholder="Emergency contact name and phone" />
              </div>
              <div class="md:col-span-2">
                <label class="form-label">Medical Notes</label>
                <textarea v-model="form.medicalNotes" class="form-input" rows="3" placeholder="Any medical conditions or notes..."></textarea>
              </div>
              <div class="md:col-span-2 flex items-center gap-2">
                <input id="active" type="checkbox" v-model="form.active" class="rounded border-gray-300 text-primary-600 focus:ring-primary-500" />
                <label for="active" class="text-sm">Active Member</label>
              </div>
            </div>
          </div>
          <div class="modal-footer flex-shrink-0">
            <button class="btn btn-outline" @click="close">Cancel</button>
            <button class="btn btn-primary" @click="save">Save</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  data: () => ({
    search: '',
    open: false,
    form: {
      id: null,
      name: '',
      memberId: '',
      phone: '',
      email: '',
      dateOfBirth: '',
      gender: 'Male',
      plan: 'Basic',
      price: 500,
      joinDate: new Date().toISOString().split('T')[0],
      nextPayment: '',
      emergencyContact: '',
      medicalNotes: '',
      active: true
    },
    rows: [
      { id: 1, name: 'John Doe', memberId: 'M001', phone: '+11123456789', email: 'john@example.com', dateOfBirth: '1990-05-15', gender: 'Male', plan: 'Premium', price: 750, joinDate: '2023-01-15', nextPayment: '2024-02-15', emergencyContact: 'Jane Doe +11123456788', medicalNotes: 'None', active: true },
      { id: 2, name: 'Jane Smith', memberId: 'M002', phone: '+11234567890', email: 'jane@example.com', dateOfBirth: '1988-08-22', gender: 'Female', plan: 'VIP', price: 1000, joinDate: '2022-11-03', nextPayment: '2024-02-03', emergencyContact: 'John Smith +11234567889', medicalNotes: 'Asthma - inhaler available', active: true },
      { id: 3, name: 'Mike Johnson', memberId: 'M003', phone: '+11345678901', email: 'mike@example.com', dateOfBirth: '1995-03-10', gender: 'Male', plan: 'Basic', price: 500, joinDate: '2021-08-21', nextPayment: '2024-01-21', emergencyContact: 'Sarah Johnson +11345678900', medicalNotes: 'None', active: false },
      { id: 4, name: 'Sarah Wilson', memberId: 'M004', phone: '+11456789012', email: 'sarah@example.com', dateOfBirth: '1992-12-05', gender: 'Female', plan: 'Student', price: 350, joinDate: '2023-09-10', nextPayment: '2024-02-10', emergencyContact: 'David Wilson +11456789011', medicalNotes: 'None', active: true },
      { id: 5, name: 'David Brown', memberId: 'M005', phone: '+11567890123', email: 'david@example.com', dateOfBirth: '1985-07-18', gender: 'Male', plan: 'Premium', price: 750, joinDate: '2022-06-15', nextPayment: '2024-02-15', emergencyContact: 'Lisa Brown +11567890122', medicalNotes: 'Knee injury - avoid heavy squats', active: true }
    ]
  }),
  computed: {
    filtered() {
      const q = this.search.trim().toLowerCase();
      if (!q) return this.rows;
      return this.rows.filter(r =>
        r.name.toLowerCase().includes(q) ||
        r.phone.toLowerCase().includes(q) ||
        r.email.toLowerCase().includes(q) ||
        r.memberId.toLowerCase().includes(q) ||
        r.plan.toLowerCase().includes(q)
      );
    },
    activeMembersCount() {
      return this.rows.filter(r => r.active).length;
    },
    expiringSoonCount() {
      const today = new Date();
      const thirtyDaysFromNow = new Date(today.getTime() + (30 * 24 * 60 * 60 * 1000));
      return this.rows.filter(r => {
        const nextPayment = new Date(r.nextPayment);
        return nextPayment <= thirtyDaysFromNow && r.active;
      }).length;
    },
    newThisMonthCount() {
      const today = new Date();
      const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
      return this.rows.filter(r => {
        const joinDate = new Date(r.joinDate);
        return joinDate >= firstDayOfMonth;
      }).length;
    }
  },
  methods: {
    openAdd() {
      this.form = { 
        id: null, 
        name: '', 
        memberId: this.generateMemberId(), 
        phone: '', 
        email: '', 
        dateOfBirth: '', 
        gender: 'Male', 
        plan: 'Basic', 
        price: 500, 
        joinDate: new Date().toISOString().split('T')[0], 
        nextPayment: '', 
        emergencyContact: '', 
        medicalNotes: '', 
        active: true 
      };
      this.open = true;
    },
    edit(row) {
      this.form = { ...row };
      this.open = true;
    },
    remove(row) {
      this.$bus.$emit('confirm:open', { title: 'Delete Member', message: `Delete ${row.name}?` });
      this.rows = this.rows.filter(r => r.id !== row.id);
      this.$bus.$emit('toast:show', { title: 'Member deleted', type: 'warning' });
    },
    viewProfile(member) {
      this.$bus.$emit('toast:show', { title: `Viewing profile for ${member.name}`, type: 'info' });
    },
    close() {
      this.open = false;
    },
    save() {
      if (this.form.id) {
        const idx = this.rows.findIndex(r => r.id === this.form.id);
        if (idx > -1) this.rows.splice(idx, 1, { ...this.form });
        this.$bus.$emit('toast:show', { title: 'Member updated', type: 'success' });
      } else {
        this.rows.push({ ...this.form, id: Date.now() });
        this.$bus.$emit('toast:show', { title: 'Member added', type: 'success' });
      }
      this.open = false;
    },
    generateMemberId() {
      const existingIds = this.rows.map(r => parseInt(r.memberId.replace('M', '')));
      const nextId = Math.max(...existingIds, 0) + 1;
      return `M${String(nextId).padStart(3, '0')}`;
    },
    getDaysUntilPayment(nextPayment) {
      if (!nextPayment) return 'N/A';
      const today = new Date();
      const paymentDate = new Date(nextPayment);
      const diffTime = paymentDate - today;
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      
      if (diffDays < 0) {
        return `${Math.abs(diffDays)} days overdue`;
      } else if (diffDays === 0) {
        return 'Due today';
      } else if (diffDays === 1) {
        return 'Due tomorrow';
      } else {
        return `${diffDays} days`;
      }
    }
  }
}
</script>


